import React, { createContext } from 'react';

import { Device, Loadable, PathwayConfig } from '../types';
import { pathwayService } from '../api/services/pathwayService';
import { useDeviceCollection } from '../hooks';

interface PathwayContextType {
  device?: Device;
  pathways?: Loadable<PathwayConfig>;
  savePathway: (pathway: PathwayConfig) => Promise<void>;
}

const loadablePathways: Loadable<PathwayConfig> = { loading: true };

const initialContext: PathwayContextType = {
  pathways: loadablePathways,
  savePathway: () => Promise.resolve(),
};

const PathwayContext = createContext<PathwayContextType>(initialContext);

interface PathwayProviderProps {
  device?: Device;
  children: React.ReactNode;
}

function PathwayProvider({ device, children }: PathwayProviderProps) {
  const { items: pathways, setItems: setPathways } = useDeviceCollection<PathwayConfig>(
    device,
    pathwayService.getPathways,
  );

  const savePathway = async (pathway: PathwayConfig) => {
    if (!device) return Promise.reject('no device');

    try {
      await pathwayService.savePathway(device.id, pathway.code, pathway);
      return setPathways({
        loading: false,
        data: pathways.data?.map((c) => (c.code === pathway.code ? pathway : c)),
      });
    } catch (err) {
      return setPathways({ loading: false, error: err as Error });
    }
  };

  return (
    <PathwayContext.Provider
      value={{
        device,
        pathways,
        savePathway,
      }}
    >
      {children}
    </PathwayContext.Provider>
  );
}

export { PathwayContext, PathwayProvider };
